import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useTranslation } from "contexts/i18nContext";
import React, { ChangeEvent, useEffect, useState } from "react";
import {
  editAccusedSummon,
  fetchAssignedProsecutor,
} from "services/advocateComplaintService";
import {
  AccusedSummon,
  AdvocateComplaints,
  AssignedProsecutor,
  Prosecutor,
} from "types";
import { LoadingIcon } from "utils/constant";
import { ADVOCATE_ACCUSED_COMPLAINT_STATUS } from "utils/constantValues";

interface DecisionModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  summonData: AccusedSummon;
  complaintData: AdvocateComplaints;
  onRefresh: any;
  onComplaintsRefresh: any;
}

export default function AccusedSummonConfirmationModal({
  isOpen,
  setIsOpen,
  complaintData,
  summonData,
  onRefresh,
  onComplaintsRefresh,
}: DecisionModalProps) {
  const { showToast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [prosecutorsData, setProsecutorsData] = useState<AssignedProsecutor[]>(
    [],
  );

  const [formState, setFormState] = useState({
    assigned_prosecutor_id: "",
    assigned_prosecutor: null as Prosecutor | null,
    status: ADVOCATE_ACCUSED_COMPLAINT_STATUS.ASSIGNED_TO_PROSECUTOR,
  });

  useEffect(() => {
    const loadProsecutors = async () => {
      try {
        setLoading(true);
        const data = await fetchAssignedProsecutor(
          user?.admin_unit,
          user?.org_unit,
          user?.access,
        );

        // Filter only users with AdvocateDisciplineProsecutor role
        const prosecutorData = data.filter(
          (item) =>
            item.roles && item.roles.includes("AdvocateDisciplineProsecutor"),
        );

        setProsecutorsData(prosecutorData);
      } catch (err) {
        // showToast("Failed to load prosecutors", "error");
      } finally {
        setLoading(false);
      }
    };
    loadProsecutors();
  }, [user, showToast]);

  const filtered = prosecutorsData?.filter((p) =>
    `${p?.user?.first_name} ${p?.user?.last_name}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (prosecutor: Prosecutor, name: string) => {
    setFormState((prevState) => ({
      ...prevState,
      [name]: prosecutor,
      assigned_prosecutor_id: prosecutor.id || "",
    }));
    setQuery("");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!summonData) return;

    setLoading(true);

    const isAccusedConfirmed = summonData?.accused_confirmed
      ? summonData?.accused_confirmed
      : false;
    const isCoordinatorConfirmed = true;

    const data = new FormData();
    data.append(
      "assigned_advocate",
      `${formState?.assigned_prosecutor?.first_name || ""} ${
        formState?.assigned_prosecutor?.last_name || ""
      }`,
    );
    data.append("status", formState.status ?? "");
    data.append("accused_confirmed", isAccusedConfirmed.toString());
    data.append("coordinator_confirmed", isCoordinatorConfirmed.toString());

    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append("accused_advocate_name", complaintData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      complaintData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      complaintData?.accused_license_number || "",
    );
    data.append(
      "assiegned_prosecutor",
      `${formState?.assigned_prosecutor?.first_name || ""} ${
        formState?.assigned_prosecutor?.last_name || ""
      }`,
    );
    data.append(
      "userid_assigned_prosecutor",
      formState?.assigned_prosecutor_id || "",
    );

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editAccusedSummon(data, summon_id, isFormData);

      onRefresh();
      onComplaintsRefresh();
      setIsOpen(false);
      showToast(t("confirmed"), "success");
      setFormState({
        assigned_prosecutor_id: "",
        assigned_prosecutor: null as Prosecutor | null,
        status: ADVOCATE_ACCUSED_COMPLAINT_STATUS.ASSIGNED_TO_PROSECUTOR,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("confirmAccusedSummon")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        {/* Previously Assigned Prosecutor Section */}
        {summonData?.assigned_advocate && (
          <section className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <h4 className="text-sm font-semibold text-yellow-800 mb-2">
              {t("previouslyAssignedProsecutor")}
            </h4>
            <div className="text-sm text-yellow-700">
              <span className="font-medium">{t("name")}:</span>{" "}
              {summonData.assigned_advocate}
            </div>
            {summonData.userid_assigned_prosecutor && (
              <div className="text-sm text-yellow-700 mt-1">
                <span className="font-medium">{t("userId")}:</span>{" "}
                {summonData.userid_assigned_prosecutor}
              </div>
            )}
          </section>
        )}

        <section className="evidence-section">
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t("assignProsecutor")}
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="assigned_prosecutor_id"
              value={formState.assigned_prosecutor_id}
              onChange={(e) => {
                handleChange(e);
                const selected = filtered?.find(
                  (p) => p.user.id === e.target.value,
                );

                if (selected && selected.user) {
                  handleSelect(selected.user, "assigned_prosecutor");
                }
              }}
            >
              <option value={""}>{t("selectProsecutor")}</option>
              {filtered?.map((p) => (
                <option key={p.user.id} value={p.user.id}>
                  {p.user?.first_name} ({p.user?.last_name})
                </option>
              ))}
            </select>
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("assign")
          )}
        </button>
      </form>
    </SideModal>
  );
}
